import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { GetTestKits, DeleteTestKit } from "../../../services/TestKitsService";
import ViewTestKit from "./ViewTestKit";
import CommonLoader from "../../common/CommonLoader";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddTestKit from "./AddTestKit";

const TestKitsTable = () => {
  const columns = [
    "Kit Id",
    "Kit Name",
    "Price",
    "Turn Around Time",
    "Status",
    "Actions",
  ];
  const columnHeaders = [
    "testKitId",
    "kitName",
    "price",
    "expectedTurnAroundTime",
    "isActive",
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [kitsData, setKitsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKit, setSelectedKit] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isAddKitDialogOpen, setIsAddKitDialogOpen] = useState(false);

  useEffect(() => {
    const fetchKits = async () => {
      try {
        setLoading(true);
        const response = await GetTestKits();
        setKitsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching kits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKits();
  }, []);

  useEffect(() => {
    const fetchKits = async () => {
      try {
        setLoading(true);
        const response = await GetTestKits();
        setKitsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching kits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKits();
  }, [isDeleted]);

  useEffect(() => {
    if (!kitsData) {
      return;
    }

    const filteredResults = kitsData.filter((kit) =>
      columnHeaders.some((header) => {
        const value = kit[header];
        if (header === "isActive") {
          const statusText = value ? "Active" : "Inactive";
          return statusText.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return value && typeof value === "string"
          ? value.toLowerCase().includes(searchTerm.toLowerCase())
          : value && typeof value === "number"
          ? value.toString().includes(searchTerm)
          : false;
      })
    );

    setFilteredData(filteredResults);
  }, [searchTerm, kitsData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (kit) => {
    setSelectedKit(kit);
  };

  const handleBackClick = () => {
    setSelectedKit(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (kit) => {
    setSelectedKit(kit.testKitId);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedKit(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
  

    try {
      setLoading(true);
      const response = await DeleteTestKit(selectedKit);
      if (response.results === true) {
        NotificationManager.success("Test kit deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setIsDeleted(!isDeleted);
        setSelectedKit(null);
      } else {
        NotificationManager.error("Error deleting test kit");
      }
    } catch (error) {
      NotificationManager.error("Error deleting test kit");
    } finally {
      setLoading(false);
    }
  };

  const handleAddKitClick = () => {
    setSelectedKit(null);
    setIsAddKitDialogOpen(true);
  };

  const handleEditKitClick = (kit) => {
    setSelectedKit(kit);
    setIsAddKitDialogOpen(true);
  };

  const handleAddKitDialogClose = () => {
    setIsAddKitDialogOpen(false);
    setSelectedKit(null);
  };

  const handleAddKitDialogSuccess = () => {
    setIsAddKitDialogOpen(false);
    setSelectedKit(null);
    setIsDeleted(!isDeleted);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const renderActions = (kit) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditKitClick(kit)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(kit)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(kit)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <span className="title">Test Kits</span>
      <hr />
      {selectedKit && !isAddKitDialogOpen && !isDeleteConfirmationOpen ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewTestKit kit={selectedKit} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddKitClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((kit, index) => (
                      <TableRow
                        key={kit.testKitId}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        <TableCell>{kit.testKitId}</TableCell>
                        <TableCell>{kit.kitName}</TableCell>
                        <TableCell>${kit.price?.toFixed(2)}</TableCell>
                        <TableCell>{kit.expectedTurnAroundTime} days</TableCell>
                        <TableCell>
                          {kit.isActive ? "Active" : "Inactive"}
                        </TableCell>
                        <TableCell>{renderActions(kit)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation" 
            message="Are you sure you want to delete this kit?"           
            color="red"
          />
        </>
      )}

      {isAddKitDialogOpen && (
        <AddTestKit
          open={isAddKitDialogOpen}
          onClose={handleAddKitDialogClose}
          onSave={handleAddKitDialogSuccess}
          selectedKit={selectedKit}
        />
      )}
    </>
  );
};

export default TestKitsTable;